import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../auth';
import type { Employee } from '../types';
import { ErrorNote, Field, Spinner, inputClass } from '../components/ui';
import { Btn, Card, CheckinStatus, PageHead, Tag } from '../components/ds';
import {
  PipSignatures, type PipParty, type PipSignature,
} from '../components/PipSignatures';

interface Pip {
  id: string;
  employeeId: string;
  employeeName: string;
  supervisorName: string;
  reason: string;
  expectedOutcome: string | null;
  startsOn: string;
  endsOn: string;
  reviewCadence: string;
  state: 'draft' | 'active' | 'completed' | 'cancelled';
  outcome: string | null;
  outcomeNotes: string | null;
  acknowledgedAt: string | null;
  // The December 2025 form's sections.
  seriesNo: string | null;
  evaluatedOn: string | null;
  latestEvaluationOn: string | null;
  evaluationPart1Score: number | null;
  evaluationPart2Score: number | null;
  evaluationOverallScore: number | null;
  pefReference: string | null;
  pipReference: string | null;
  recordOfInfractions: string | null;
  targetScore: number | null;
  initialReviewOn: string | null;
  finalReviewOn: string | null;
  skillsToImprove: string | null;
  officerResponsibility: string | null;
  additionalRemarks: string | null;
  acknowledgements: PipSignature[];
  /** What THIS viewer may sign right now — decided by the database. */
  signableParties: PipParty[];
  milestoneCount: number;
  milestonesMet: number;
  milestonesPending: number;
}

interface Milestone {
  id: string;
  sequence: number;
  description: string;
  successCriteria: string | null;
  dueOn: string;
  met: boolean | null;
  assessmentNotes: string | null;
  assessedBy: string | null;
}

interface PipReview {
  id: string;
  reviewDate: string;
  progressSummary: string;
  statusFlag: 'on_track' | 'at_risk' | 'off_track';
  employeeComment: string | null;
  reviewedBy: string;
  kind: 'interim' | 'initial' | 'final';
  score: number | null;
}

/**
 * Performance Improvement Plans.
 *
 * This screen shows only what the viewer is permitted to see, and the database
 * enforces it: a PIP is visible to its subject, their direct supervisor, HR, and
 * the department or area head over their unit (who must read it to sign it). A
 * skip-level manager is deliberately not on that list. An employee reaching this
 * page sees their own plan and nothing else, which is deliberate: someone on a
 * PIP must be able to read it.
 *
 * Laid out as the December 2025 form is: evaluation results, improvement goals,
 * action plan, then the four signatures.
 */
export default function Pips() {
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const pips = useQuery({ queryKey: ['pips'], queryFn: () => api<Pip[]>('/pips') });

  if (pips.isLoading) return <Spinner />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      {/* The nav calls this "PIPs" for width; the heading spells it out, because
          the abbreviation is jargon to the employee who has just been put on
          one. */}
      <PageHead title="Performance improvement plans">
        <Btn variant="primary" onClick={() => setCreating((v) => !v)}>New PIP</Btn>
      </PageHead>

      {creating && <NewPipForm onDone={() => setCreating(false)} />}

      <Card kicker={`Plans (${pips.data?.length ?? 0})`}>
        <p className="mb-3 text-xs t-muted">
          Visible to the employee, their direct supervisor, HR, and the department
          or area head over their unit.
        </p>
        {pips.data?.length === 0 ? (
          <p className="card-body" style={{ margin: 0 }}>No plans on record.</p>
        ) : (
          <ul>
            {pips.data?.map((p) => (
              <li key={p.id} className="py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <button
                      className="font-medium text-muted hover:underline"
                      onClick={() => setOpenId(openId === p.id ? null : p.id)}
                    >
                      {p.employeeName}
                    </button>
                    <p className="text-xs t-muted">
                      {p.seriesNo && <>{p.seriesNo} · </>}
                      {p.startsOn} → {p.endsOn} · supervisor {p.supervisorName} ·{' '}
                      {p.milestonesMet}/{p.milestoneCount} milestones met
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {p.state === 'active' && p.acknowledgements.length < 4 && (
                      <span className="text-xs text-muted">
                        {p.acknowledgements.length} of 4 signed
                      </span>
                    )}
                    <span className="tag">
                      {p.state}{p.outcome ? ` · ${p.outcome}` : ''}
                    </span>
                  </div>
                </div>
                {openId === p.id && <PipDetail pip={p} />}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function PipDetail({ pip }: { pip: Pip }) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Employee>('/employees/me') });

  const milestones = useQuery({
    queryKey: ['pip-milestones', pip.id],
    queryFn: () => api<Milestone[]>(`/pips/${pip.id}/milestones`),
  });
  const reviews = useQuery({
    queryKey: ['pip-reviews', pip.id],
    queryFn: () => api<PipReview[]>(`/pips/${pip.id}/reviews`),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['pips'] });
    void qc.invalidateQueries({ queryKey: ['pip-milestones', pip.id] });
    void qc.invalidateQueries({ queryKey: ['pip-reviews', pip.id] });
  };

  const act = useMutation({
    mutationFn: (input: { path: string; body?: unknown }) =>
      api(`/pips/${pip.id}/${input.path}`, { method: 'POST', body: input.body }),
    onSuccess: invalidate,
  });

  const assess = useMutation({
    mutationFn: (input: { id: string; met: boolean }) =>
      api(`/pip-milestones/${input.id}/assess`, {
        method: 'POST', body: { met: input.met },
      }),
    onSuccess: invalidate,
  });

  const isSubject = me.data?.id === pip.employeeId;

  const sign = useMutation({
    mutationFn: (party: PipParty) =>
      api(`/pips/${pip.id}/acknowledge`, { method: 'POST', body: { party } }),
    onSuccess: invalidate,
  });

  // The scores recorded at the two reviews the schedule names. A scheduled date
  // with no score yet is shown as scheduled, not as missing.
  const recorded = (kind: 'initial' | 'final') =>
    reviews.data?.find((r) => r.kind === kind);
  const initial = recorded('initial');
  const final = recorded('final');

  const hasResults = [
    pip.seriesNo, pip.evaluatedOn, pip.latestEvaluationOn, pip.evaluationPart1Score,
    pip.evaluationPart2Score, pip.evaluationOverallScore, pip.pefReference,
    pip.pipReference, pip.recordOfInfractions,
  ].some((v) => v !== null && v !== '');

  return (
    <div className="mt-3 space-y-4 panel-tint p-4">
      {hasResults && (
        <FormSection title="Evaluation results">
          <FormRow label="Series no." value={pip.seriesNo} />
          <FormRow label="Date of evaluation" value={pip.evaluatedOn} />
          <FormRow label="Date of latest evaluation" value={pip.latestEvaluationOn} />
          <FormRow label="Score — part 1" value={pip.evaluationPart1Score} />
          <FormRow label="Score — part 2" value={pip.evaluationPart2Score} />
          <FormRow label="Overall score" value={pip.evaluationOverallScore} />
          <FormRow label="PEF reference" value={pip.pefReference} />
          <FormRow label="PIP reference" value={pip.pipReference} />
          <FormRow label="Record of infractions" value={pip.recordOfInfractions} />
        </FormSection>
      )}

      <FormSection title="Area of concern">
        <p className="text-sm">{pip.reason}</p>
      </FormSection>

      {(pip.targetScore !== null || pip.initialReviewOn || pip.finalReviewOn) && (
        <FormSection title="Improvement goals">
          <FormRow label="Target score" value={pip.targetScore} />
          <FormRow
            label="Initial review"
            value={reviewLine(pip.initialReviewOn, initial)} />
          <FormRow
            label="Final review"
            value={reviewLine(pip.finalReviewOn, final)} />
        </FormSection>
      )}

      {(pip.skillsToImprove || pip.officerResponsibility || pip.expectedOutcome
        || pip.additionalRemarks) && (
        <FormSection title="Action plan">
          <FormRow label="Skills / behaviour to improve" value={pip.skillsToImprove} />
          <FormRow label="Officer's responsibility" value={pip.officerResponsibility} />
          <FormRow label="Expected outcome" value={pip.expectedOutcome} />
          <FormRow label="Additional remarks" value={pip.additionalRemarks} />
        </FormSection>
      )}

      <div>
        <h3 className="mb-2 text-xs font-semibold tracking-wide t-muted uppercase">
          Actions to be taken, and when
        </h3>
        <ul className="space-y-2">
          {milestones.data?.map((m) => (
            <li key={m.id} className="flex flex-wrap items-start justify-between gap-2 rounded border border-divider bg-surface px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm">{m.description}</p>
                {m.successCriteria && (
                  <p className="text-xs t-muted">Criteria: {m.successCriteria}</p>
                )}
                <p className="text-xs t-muted">
                  due {m.dueOn}
                  {m.assessedBy && ` · assessed by ${m.assessedBy}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {m.met === null ? (
                  pip.state === 'active' && !isSubject && (
                    <>
                      <Btn onClick={() => assess.mutate({ id: m.id, met: true })}>Met</Btn>
                      <Btn
                              onClick={() => assess.mutate({ id: m.id, met: false })}>
                        Not met
                      </Btn>
                    </>
                  )
                ) : (
                  // A met and a not-met milestone rendered identically here: the
                  // conversion left both branches of the class the same, so the
                  // only difference was the word. On a PIP — where the whole
                  // point is whether the milestones were met — that is the one
                  // distinction that has to be visible.
                  <Tag tone={m.met ? 'accent' : 'outline'}>
                    {m.met ? 'met' : 'not met'}
                  </Tag>
                )}
              </div>
            </li>
          ))}
        </ul>
        <ErrorNote error={assess.error} />
      </div>

      <div>
        <h3 className="mb-2 text-xs font-semibold tracking-wide t-muted uppercase">
          Reviews
        </h3>
        {reviews.data?.length === 0 ? (
          <p className="text-sm t-muted">No reviews recorded.</p>
        ) : (
          <ol className="space-y-2">
            {reviews.data?.map((r) => (
              <li key={r.id} className="border-l-2 border-divider pl-3">
                <div className="flex flex-wrap items-center gap-2">
                  <CheckinStatus status={r.statusFlag} />
                  {r.kind !== 'interim' && (
                    <Tag tone="solid">
                      {r.kind} review{r.score !== null ? ` · ${r.score}` : ''}
                    </Tag>
                  )}
                  <span className="text-xs t-muted">{r.reviewDate} · {r.reviewedBy}</span>
                </div>
                <p className="mt-1 text-sm">{r.progressSummary}</p>
                {r.employeeComment && (
                  <p className="mt-1 text-sm t-muted italic">
                    Employee: {r.employeeComment}
                  </p>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>

      {pip.state === 'active' && !isSubject && (
        <PipReviewForm pipId={pip.id} onDone={invalidate} />
      )}

      <PipSignatures
        state={pip.state}
        signatures={pip.acknowledgements}
        signable={pip.signableParties}
        pending={sign.isPending}
        onSign={(party) => sign.mutate(party)}
      />
      <ErrorNote error={sign.error} />

      <div className="flex flex-wrap gap-2 no-print">
        {pip.state === 'draft' && !isSubject && (
          <Btn variant="primary" onClick={() => act.mutate({ path: 'activate' })}>
            Activate plan
          </Btn>
        )}
        {pip.state === 'active' && !isSubject && (
          <>
            <Btn onClick={() => act.mutate({
              path: 'close', body: { outcome: 'successful' } })}>
              Close — successful
            </Btn>
            <Btn onClick={() => act.mutate({
              path: 'close', body: { outcome: 'extended' } })}>
              Close — extended
            </Btn>
            <Btn onClick={() => act.mutate({
              path: 'close', body: { outcome: 'unsuccessful' } })}>
              Close — unsuccessful
            </Btn>
          </>
        )}
      </div>
      <ErrorNote error={act.error} />
    </div>
  );
}

/** A titled block of the printed form. */
function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold tracking-wide t-muted uppercase">{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

/** One labelled line of the form. Absent when the box was left blank. */
function FormRow({ label, value }: { label: string; value: string | number | null }) {
  if (value === null || value === '') return null;
  return (
    <p className="text-sm">
      <span className="font-medium">{label}: </span>{value}
    </p>
  );
}

/** "scheduled 2026-04-01 · scored 62.5", or just the schedule while it is ahead. */
function reviewLine(
  scheduled: string | null, recorded: PipReview | undefined,
): string | null {
  if (!scheduled && !recorded) return null;
  const parts: string[] = [];
  if (scheduled) parts.push(`scheduled ${scheduled}`);
  if (recorded && recorded.score !== null) parts.push(`scored ${recorded.score}`);
  return parts.join(' · ');
}

function PipReviewForm({ pipId, onDone }: { pipId: string; onDone: () => void }) {
  const [reviewDate, setReviewDate] = useState(new Date().toISOString().slice(0, 10));
  const [statusFlag, setStatusFlag] = useState('on_track');
  const [progressSummary, setProgressSummary] = useState('');
  const [employeeComment, setEmployeeComment] = useState('');
  const [kind, setKind] = useState<'interim' | 'initial' | 'final'>('interim');
  const [score, setScore] = useState('');

  const save = useMutation({
    mutationFn: () =>
      api(`/pips/${pipId}/reviews`, {
        method: 'POST',
        body: {
          reviewDate, statusFlag, progressSummary: progressSummary.trim(),
          employeeComment: employeeComment.trim() || undefined,
          kind,
          score: kind !== 'interim' && score !== '' ? Number(score) : undefined,
        },
      }),
    onSuccess: () => {
      setProgressSummary(''); setEmployeeComment(''); setKind('interim'); setScore('');
      onDone();
    },
  });

  return (
    <form className="grid gap-3 rounded-md border border-divider bg-surface p-3 sm:grid-cols-2"
          onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <Field label="Review date">
        <input type="date" required className={inputClass} value={reviewDate}
               onChange={(e) => setReviewDate(e.target.value)} />
      </Field>
      <Field label="Status">
        <select className={inputClass} value={statusFlag}
                onChange={(e) => setStatusFlag(e.target.value)}>
          <option value="on_track">On track</option>
          <option value="at_risk">At risk</option>
          <option value="off_track">Off track</option>
        </select>
      </Field>
      <Field label="Review" hint="The form's schedule names an initial and a final review.">
        <select className={inputClass} value={kind}
                onChange={(e) => setKind(e.target.value as typeof kind)}>
          <option value="interim">Ordinary review</option>
          <option value="initial">Initial review</option>
          <option value="final">Final review</option>
        </select>
      </Field>
      {kind !== 'interim' && (
        <Field label="Score" hint="Out of 100. An initial or final review records one.">
          <input type="number" min={0} max={100} step="0.01" required
                 className={inputClass} value={score}
                 onChange={(e) => setScore(e.target.value)} />
        </Field>
      )}
      <div className="sm:col-span-2">
        <Field label="Progress summary">
          <textarea rows={2} required className={inputClass} value={progressSummary}
                    onChange={(e) => setProgressSummary(e.target.value)} />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Employee comment"
               hint="Record the employee's own response — a one-sided PIP record is weak evidence">
          <textarea rows={2} className={inputClass} value={employeeComment}
                    onChange={(e) => setEmployeeComment(e.target.value)} />
        </Field>
      </div>
      <div className="sm:col-span-2 flex items-center gap-3">
        <Btn type="submit" variant="primary" disabled={save.isPending}>Record review</Btn>
        <span className="text-xs t-muted">Reviews are permanent and cannot be edited.</span>
      </div>
      {save.error ? <div className="sm:col-span-2"><ErrorNote error={save.error} /></div> : null}
    </form>
  );
}

function NewPipForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const reports = useQuery({
    queryKey: ['reports'],
    queryFn: () => api<Employee[]>('/employees/me/reports'),
  });

  const [employeeId, setEmployeeId] = useState('');
  const [reason, setReason] = useState('');
  const [expectedOutcome, setExpectedOutcome] = useState('');
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [reviewCadence, setReviewCadence] = useState('biweekly');
  const [milestones, setMilestones] = useState([
    { description: '', successCriteria: '', dueOn: '' },
  ]);

  // The December 2025 form's sections. All optional, as on the paper form.
  const [extra, setExtra] = useState({
    seriesNo: '', evaluatedOn: '', latestEvaluationOn: '',
    evaluationPart1Score: '', evaluationPart2Score: '', evaluationOverallScore: '',
    pefReference: '', pipReference: '', recordOfInfractions: '',
    targetScore: '', initialReviewOn: '', finalReviewOn: '',
    skillsToImprove: '', officerResponsibility: '', additionalRemarks: '',
  });
  const setX = (k: keyof typeof extra, v: string) =>
    setExtra((prev) => ({ ...prev, [k]: v }));

  /** Blank boxes are not sent: absent on the form is absent in the record. */
  const optionalBody = () => {
    const text = (v: string) => (v.trim() === '' ? undefined : v.trim());
    const num = (v: string) => (v.trim() === '' ? undefined : Number(v));
    return {
      seriesNo: text(extra.seriesNo),
      evaluatedOn: text(extra.evaluatedOn),
      latestEvaluationOn: text(extra.latestEvaluationOn),
      evaluationPart1Score: num(extra.evaluationPart1Score),
      evaluationPart2Score: num(extra.evaluationPart2Score),
      evaluationOverallScore: num(extra.evaluationOverallScore),
      pefReference: text(extra.pefReference),
      pipReference: text(extra.pipReference),
      recordOfInfractions: text(extra.recordOfInfractions),
      targetScore: num(extra.targetScore),
      initialReviewOn: text(extra.initialReviewOn),
      finalReviewOn: text(extra.finalReviewOn),
      skillsToImprove: text(extra.skillsToImprove),
      officerResponsibility: text(extra.officerResponsibility),
      additionalRemarks: text(extra.additionalRemarks),
    };
  };

  const create = useMutation({
    mutationFn: () =>
      api('/pips', {
        method: 'POST',
        body: {
          employeeId, reason: reason.trim(),
          expectedOutcome: expectedOutcome.trim() || undefined,
          startsOn, endsOn, reviewCadence,
          ...optionalBody(),
          milestones: milestones.map((m) => ({
            description: m.description.trim(),
            successCriteria: m.successCriteria.trim() || undefined,
            dueOn: m.dueOn,
          })),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pips'] });
      onDone();
    },
  });

  const patch = (i: number, p: Partial<(typeof milestones)[number]>) =>
    setMilestones((prev) => prev.map((m, idx) => (idx === i ? { ...m, ...p } : m)));

  return (
    <Card kicker="New performance improvement plan">
      <form className="grid gap-4 sm:grid-cols-2"
            onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <Field label="Employee">
          <select className={inputClass} value={employeeId} required
                  onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">Select…</option>
            {reports.data?.map((r) => (
              <option key={r.id} value={r.id}>{r.firstName} {r.lastName}</option>
            ))}
          </select>
        </Field>

        <Field label="Review cadence">
          <select className={inputClass} value={reviewCadence}
                  onChange={(e) => setReviewCadence(e.target.value)}>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Bi-weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </Field>

        <div className="sm:col-span-2">
          <Field
            label="Area of concern"
            hint="The areas where expectations have not been met. Be specific and factual — this is an employment record and may be read back later."
          >
            <textarea rows={3} required minLength={10} className={inputClass} value={reason}
                      onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <Field label="Record of infraction/s"
                 hint="You may ask HCM for assistance with the record.">
            <textarea rows={2} className={inputClass} value={extra.recordOfInfractions}
                      onChange={(e) => setX('recordOfInfractions', e.target.value)} />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <h3 className="text-sm font-medium">Personal evaluation results</h3>
          <p className="text-xs t-muted">
            From the evaluation that led to this plan. Typed in, as on the paper form.
          </p>
        </div>
        <Field label="Series no.">
          <input className={inputClass} value={extra.seriesNo}
                 onChange={(e) => setX('seriesNo', e.target.value)} />
        </Field>
        <Field label="Date of evaluation">
          <input type="date" className={inputClass} value={extra.evaluatedOn}
                 onChange={(e) => setX('evaluatedOn', e.target.value)} />
        </Field>
        <Field label="Date of latest evaluation">
          <input type="date" className={inputClass} value={extra.latestEvaluationOn}
                 onChange={(e) => setX('latestEvaluationOn', e.target.value)} />
        </Field>
        <Field label="PEF reference">
          <input className={inputClass} value={extra.pefReference}
                 onChange={(e) => setX('pefReference', e.target.value)} />
        </Field>
        <Field label="Score — part 1">
          <input type="number" min={0} max={100} step="0.01" className={inputClass}
                 value={extra.evaluationPart1Score}
                 onChange={(e) => setX('evaluationPart1Score', e.target.value)} />
        </Field>
        <Field label="Score — part 2">
          <input type="number" min={0} max={100} step="0.01" className={inputClass}
                 value={extra.evaluationPart2Score}
                 onChange={(e) => setX('evaluationPart2Score', e.target.value)} />
        </Field>
        <Field label="Overall score">
          <input type="number" min={0} max={100} step="0.01" className={inputClass}
                 value={extra.evaluationOverallScore}
                 onChange={(e) => setX('evaluationOverallScore', e.target.value)} />
        </Field>
        <Field label="PIP reference" hint="A previous plan, if any.">
          <input className={inputClass} value={extra.pipReference}
                 onChange={(e) => setX('pipReference', e.target.value)} />
        </Field>

        <div className="sm:col-span-2">
          <h3 className="text-sm font-medium">Improvement goals</h3>
        </div>
        <Field label="Target score" hint="Out of 100.">
          <input type="number" min={0} max={100} step="0.01" className={inputClass}
                 value={extra.targetScore}
                 onChange={(e) => setX('targetScore', e.target.value)} />
        </Field>
        <div />
        <Field label="Initial review date">
          <input type="date" className={inputClass} value={extra.initialReviewOn}
                 onChange={(e) => setX('initialReviewOn', e.target.value)} />
        </Field>
        <Field label="Final review date">
          <input type="date" className={inputClass} value={extra.finalReviewOn}
                 onChange={(e) => setX('finalReviewOn', e.target.value)} />
        </Field>

        <div className="sm:col-span-2">
          <h3 className="text-sm font-medium">Action plan</h3>
        </div>
        <div className="sm:col-span-2">
          <Field label="Skills / behaviour to improve"
                 hint="The skills, behaviour, duties or actions that need to improve. Be specific.">
            <textarea rows={2} className={inputClass} value={extra.skillsToImprove}
                      onChange={(e) => setX('skillsToImprove', e.target.value)} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Officer's responsibility"
                 hint="Support, training or resources needed to improve the employee's performance.">
            <textarea rows={2} className={inputClass} value={extra.officerResponsibility}
                      onChange={(e) => setX('officerResponsibility', e.target.value)} />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <Field label="Expected outcome"
                 hint="The overall outcome if the plan is achieved, or not achieved, by the plan end date.">
            <textarea rows={2} className={inputClass} value={expectedOutcome}
                      onChange={(e) => setExpectedOutcome(e.target.value)} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Additional remarks">
            <textarea rows={2} className={inputClass} value={extra.additionalRemarks}
                      onChange={(e) => setX('additionalRemarks', e.target.value)} />
          </Field>
        </div>

        <Field label="Starts">
          <input type="date" required className={inputClass} value={startsOn}
                 onChange={(e) => setStartsOn(e.target.value)} />
        </Field>
        <Field label="Ends">
          <input type="date" required className={inputClass} value={endsOn}
                 onChange={(e) => setEndsOn(e.target.value)} />
        </Field>

        <div className="sm:col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-medium">
              Actions to be taken, and by when{' '}
              <span className="text-xs font-normal t-muted">(at least one required)</span>
            </h3>
            <Btn type="button"
                    onClick={() => setMilestones((p) => [...p, { description: '', successCriteria: '', dueOn: '' }])}>
              Add milestone
            </Btn>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            {milestones.map((m, i) => (
              <div key={i} className="grid gap-3 rounded-md border border-divider p-3 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <Field label="Description">
                    <input required className={inputClass} value={m.description}
                           onChange={(e) => patch(i, { description: e.target.value })} />
                  </Field>
                </div>
                <Field label="Due">
                  <input type="date" required className={inputClass} value={m.dueOn}
                         onChange={(e) => patch(i, { dueOn: e.target.value })} />
                </Field>
                <div className="sm:col-span-3">
                  <Field label="Success criteria" hint="How will this be judged met or not met?">
                    <input className={inputClass} value={m.successCriteria}
                           onChange={(e) => patch(i, { successCriteria: e.target.value })} />
                  </Field>
                </div>
                {milestones.length > 1 && (
                  <div className="sm:col-span-3">
                    <Btn type="button"
                            onClick={() => setMilestones((p) => p.filter((_, idx) => idx !== i))}>
                      Remove
                    </Btn>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="sm:col-span-2 flex gap-3">
          <Btn type="submit" variant="primary" disabled={create.isPending}>
            Create as draft
          </Btn>
          <Btn type="button" onClick={onDone}>Cancel</Btn>
        </div>
        {create.error ? <div className="sm:col-span-2"><ErrorNote error={create.error} /></div> : null}
      </form>
    </Card>
  );
}
